'use client';

import { TimeslotItem } from '@/types/Broadcast';
import React, { useEffect, useState, useRef, useMemo } from 'react';

import styles from '@/styles/components/CurrentNextSchedule.module.scss';
import Link from 'next/link';
import { generateLocalizedURL } from '@/utils/url';
<h2 className={styles.scheduleTitle}>جدول پخش</h2>
import { Language } from '@/types/language';

type CurrentNextScheduleProps = {
  programs: {
    date: string;
    items: TimeslotItem[];
  }[];
  serverTime: string;
  maxPrograms?: number;
  outlet: 'iitv' | 'aitv';
}

const parseTimeToSeconds = (timeString: string): number => {
  const [hours = 0, minutes = 0, seconds = 0] = timeString.split(':').map(Number);
  return hours * 3600 + minutes * 60 + seconds;
}

const CurrentNextSchedule: React.FC<CurrentNextScheduleProps> = ({
  programs,
  serverTime,
  maxPrograms = 40,
  outlet
}) => {
  const [currentTime, setCurrentTime] = useState(new Date(serverTime));
  const [currentProgram, setCurrentProgram] = useState<TimeslotItem | null>(null);
  const [selectedTimezone, setSelectedTimezone] = useState(outlet === 'iitv' ? 'Asia/Tehran' : 'Asia/Kabul');
  const timelineRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const displayedPrograms = useMemo(() => {
    const todayStart = new Date(serverTime);
    todayStart.setUTCHours(0, 0, 0, 0);

    const tomorrowStart = new Date(todayStart);
    tomorrowStart.setUTCDate(todayStart.getUTCDate() + 1);

    const currentUTC = new Date(serverTime).getTime();

    const today = programs.find(({ date }) => new Date(date).toDateString() === todayStart.toDateString());
    const todayPrograms = today
      ? today.items.filter(({ broadcastTime, duration }) => {
        const broadcastStart = new Date(broadcastTime).getTime();
        const broadcastEnd = broadcastStart + parseTimeToSeconds(duration) * 1000;
        return broadcastEnd > currentUTC;
      })
      : [];

    if (todayPrograms.length >= 5) return todayPrograms;

    const tomorrow = programs.find(({ date }) => new Date(date).getTime() >= tomorrowStart.getTime());
    const tomorrowPrograms = tomorrow ? tomorrow.items.slice(0, 10) : [];

    return [...todayPrograms, ...tomorrowPrograms];
  }, [programs, serverTime, maxPrograms]);

  useEffect(() => {
    const current = displayedPrograms.find(({ broadcastTime, duration }) => {
      const broadcastStart = new Date(broadcastTime).getTime();
      const broadcastEnd = broadcastStart + parseTimeToSeconds(duration) * 1000;
      return currentTime.getTime() >= broadcastStart && currentTime.getTime() < broadcastEnd;
    });
    setCurrentProgram(current || null);
  }, [displayedPrograms, currentTime]);

  const formatTimeForDisplay = (time: string): string => {
    return new Intl.DateTimeFormat('fa-IR', {
      hour: 'numeric',
      minute: 'numeric',
      timeZone: selectedTimezone,
    }).format(new Date(time));
  };

  const calculateProgress = (broadcastTime: string, duration: string): string => {
    const startTime = new Date(broadcastTime).getTime();
    const endTime = startTime + parseTimeToSeconds(duration) * 1000;
    const currentTimeValue = currentTime.getTime();

    if (currentTimeValue < startTime) {
      return '0%';
    } else if (currentTimeValue > endTime) {
      return '100%';
    } else {
      const progress = (currentTimeValue - startTime) / (endTime - startTime) * 100;
      return `${progress.toFixed(2)}%`;
    }
  };

  const scrollTimeline = (direction: 'left' | 'right') => {
    if (timelineRef.current) {
      const { clientWidth } = timelineRef.current;
      const scrollAmount = direction === 'left' ? -clientWidth / 2 : clientWidth / 2;
      timelineRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (currentProgram && timelineRef.current) {
      const currentElement = document.getElementById(`${currentProgram.id}`);
      if (currentElement) {
        currentElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }
  }, [currentProgram]);

  const getLanguagePath = (language: string) => {
    switch (language) {
      case 'farsi':
        return '';
      case 'english':
        return '/en';
      case 'pashto':
        return '/pa';
      default:
        return '';
    }
  };

  return (
    <>
      <section className={styles.schedule}>
        <header className={styles.scheduleHeader}>
          <Link target="_blank" href={generateLocalizedURL('tvschedule', 'farsi' as Language, false)} prefetch={false}>
            <h2 className={styles.scheduleTitle}>جدول پخش</h2>
          </Link>
        </header>
        <div
          ref={timelineRef}
          className={styles.timeline}
          style={{ scrollbarWidth: 'auto', overflowX: 'auto' }}
        >
          {displayedPrograms.map((slot: TimeslotItem) => {
            const { id, broadcastTime, duration, programme } = slot;

            const isCurrent = currentProgram?.id === id;

            return (
              <div
                key={id}
                id={id}
                className={`${styles.timelineItem} ${isCurrent ? styles.current : ''}`}>
                {isCurrent &&
                  <div className={styles.progress}
                    style={{ width: calculateProgress(broadcastTime, duration) }} />
                }
                {isCurrent ?
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontWeight: 'bold' }}>
                    <div className={styles.time}>در حال پخش</div>
                    <div className={styles.time}>
                      {formatTimeForDisplay(broadcastTime)}
                    </div>
                  </div> : <time className={styles.time}>
                    {formatTimeForDisplay(broadcastTime)}
                  </time>}
                {(programme.slug && programme.language && !programme.slug.startsWith('aitv')) ? (
                  <Link className={styles.title} target='_blank' href={`/program${getLanguagePath(programme.language as string)}/${programme.slug}`} prefetch={false}>
                    <span>{programme.title}</span>
                  </Link>
                ) : <span className={styles.title}>{programme.title}</span>}
              </div>
            );
          })}
        </div>
        <footer className={styles.scheduleFooter}>
          <button
            className={`${styles.scrollButton} ${styles.right}`}
            onClick={() => scrollTimeline('right')}
          />
          <button
            onClick={() => scrollTimeline('left')}
            className={`${styles.scrollButton} ${styles.left}`}
          />
        </footer>
      </section>
      {process.env.OUTLET == 'iitv' ? <section className={styles.notice}>
        <Link target='_blank' href='https://www.youtube.com/@IRANINTL' prefetch={false}>
          <p>پخش زنده و سایر برنامه‌های ایران‌اینترنشنال را می‌توانید از طریق کانال رسمی این شبکه در یوتیوب تماشا کنید.</p>
        </Link>
      </section> :
      <section className={styles.notice}>
      <Link target='_blank' href='https://www.youtube.com/@afintltv' prefetch={false}>
        <p>پخش زنده و سایر برنامه‌های افغانستان اینترنشنال را می‌توانید از طریق کانال رسمی این شبکه در یوتیوب تماشا کنید.</p>
      </Link>
    </section>
      }

      <section className={styles.box}>
        <Link target="_blank" href={`/how-to-watch`} prefetch={false}>
          <p>برای دسترسی به اطلاعات جامع دربارهٔ تمامی روش‌های تماشای ایران‌اینترنشنال، از پخش ماهواره‌ای گرفته تا اپلیکیشن‌های موبایل، به صفحهٔ «راه‌های دیدن ما» مراجعه کنید. در این بخش، راهنما و جزئیات کاملی درخصوص نحوهٔ دریافت شبکه از طریق هریک از بسترهای موجود ارائه شده است.</p>
        </Link>
      </section>
    </>
  );
}

export default CurrentNextSchedule;